import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import { MainLayout } from './components/layout/main-layout'
import { clearTokens, getTokenPayload, isAuthenticated } from './lib/auth'

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: MainLayout,
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: { message: undefined },
      })
    }

    const payload = getTokenPayload()
    if (!payload || payload.realm !== 'partner') {
      clearTokens()
      throw redirect({
        to: '/login',
        search: { message: 'Access denied — not a partner account' },
      })
    }
  },
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('./pages/login'), 'LoginPage'),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      message: (search['message'] as string) || undefined,
    }
  },
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/dashboard' })
    }
  },
})

const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' })
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/dashboard',
  component: lazyRouteComponent(() => import('./pages/dashboard'), 'DashboardPage'),
})

const plansRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/plans',
  component: lazyRouteComponent(() => import('./pages/plans'), 'PlansPage'),
})

const tenantsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tenants',
  component: lazyRouteComponent(() => import('./pages/tenants'), 'TenantsPage'),
})

const invoicesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/invoices',
  component: lazyRouteComponent(() => import('./pages/invoices'), 'InvoicesPage'),
})

const tenantDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tenants/$tenantId',
  component: lazyRouteComponent(() => import('./pages/tenants/detail'), 'TenantDetailPage'),
})

const commissionsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/commissions',
  component: lazyRouteComponent(() => import('./pages/commissions'), 'CommissionsPage'),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      status: (search['status'] as string) || undefined,
    } as { status?: string }
  },
})

const agreementsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/agreements',
  component: lazyRouteComponent(() => import('./pages/agreements'), 'AgreementsPage'),
})

const teamRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/team',
  component: lazyRouteComponent(() => import('./pages/team'), 'TeamPage'),
})

const rolesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/roles',
  component: lazyRouteComponent(() => import('./pages/roles'), 'RolesPage'),
})

const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./pages/settings'), 'SettingsPage'),
})

const referralRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/referral',
  component: lazyRouteComponent(() => import('./pages/referral'), 'ReferralPage'),
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    indexRoute,
    dashboardRoute,
    plansRoute,
    tenantsRoute,
    tenantDetailRoute,
    invoicesRoute,
    commissionsRoute,
    agreementsRoute,
    teamRoute,
    rolesRoute,
    settingsRoute,
    referralRoute,
  ]),
])

// Every page is its own chunk (lazyRouteComponent above), so the first load only
// downloads the layout and the current page. 'intent' preloads a page's chunk when
// the user hovers or focuses a link to it, so navigating still feels instant.
export const router = createRouter({ routeTree, defaultPreload: 'intent' })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
